// Eşleştirme tablosu alt satırı — "Kırılım olarak ekle" (spec §7.3): görünür etiketli üç alan
// Üst gösterge (aranabilir; yol ipucu) / Grup (12 boyut + "Diğer" → serbest grup adı) / Üye adı
// (varsayılan kaynak etiketinden üst öneki soyulmuş). Eksik alan satırda işaretlenir; alan yazımı
// `patchBreakdownFields` (lib/kpi-import-breakdown) — bileşen yalnız görünüm + olay.
// "Diğer" grup adı yazılırken ad bilinen bir boyuta denk gelse de (ör. "Cinsiyet") serbest alan
// açık kalır; bilinen boyut yalnız alandan çıkınca (blur) ya da seçiciden açıkça seçilince benimsenir.

import { useId, useState } from "react";
import { type KpiMappingRow } from "@mahir/shared";
import {
  breakdownDraft,
  breakdownProblem,
  dimensionSelectValue,
  type BreakdownFieldPatch,
} from "../../../lib/kpi-import-breakdown";
import { dimensionOptions } from "../../../lib/kpi-breakdown-group-form";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import { SearchSelect, type SearchSelectOption } from "../../common/SearchSelect";
import { inputStyle } from "../kpi-form-styles";

const b = KPI_IMPORT_COPY.breakdown;
const NO_USED: ReadonlySet<string> = new Set();

const fieldLabel: React.CSSProperties = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "var(--color-text-2)",
};

export function ImportBreakdownFields(p: {
  row: KpiMappingRow;
  parentOptions: readonly SearchSelectOption[];
  onPatch: (patch: BreakdownFieldPatch) => void;
}): JSX.Element {
  const { row, parentOptions, onPatch } = p;
  const id = useId();
  const bd = breakdownDraft(row, null);
  const problem = breakdownProblem(row.breakdown);
  const [otherOpen, setOtherOpen] = useState(() => dimensionSelectValue(bd.dimension) === "diger");
  const dimValue = otherOpen ? "diger" : dimensionSelectValue(bd.dimension);
  const errId = `${id}-err`;
  return (
    <div
      className="flex items-start gap-3 flex-wrap"
      role="group"
      aria-label={`${row.rowLabel} — ${KPI_IMPORT_COPY.mapping.actions.create_breakdown}`}
      aria-describedby={problem ? errId : undefined}
      data-testid="kpi-import-bd-fields"
    >
      <div className="flex flex-col gap-1" style={{ minWidth: 240 }}>
        <label htmlFor={`${id}-parent`} style={fieldLabel}>
          {b.fields.parent}
        </label>
        <SearchSelect
          id={`${id}-parent`}
          ariaLabel={`${b.fields.parent}: ${row.rowLabel}`}
          value={bd.parentCode}
          options={parentOptions}
          placeholder={b.placeholders.parent}
          onChange={(code) => {
            const o = parentOptions.find((x) => x.value === code);
            if (o) onPatch({ parent: { code: o.value, label: o.label } });
          }}
        />
      </div>
      <div className="flex flex-col gap-1" style={{ minWidth: 200 }}>
        <label htmlFor={`${id}-dim`} style={fieldLabel}>
          {b.fields.group}
        </label>
        <SearchSelect
          id={`${id}-dim`}
          ariaLabel={`${b.fields.group}: ${row.rowLabel}`}
          value={dimValue}
          options={dimensionOptions(NO_USED)}
          placeholder={b.placeholders.group}
          showCode={false}
          onChange={(d) => {
            setOtherOpen(d === "diger");
            onPatch({ dimension: d });
          }}
        />
        {dimValue === "diger" ? (
          <>
            <label htmlFor={`${id}-other`} style={fieldLabel}>
              {b.fields.otherGroupLabel}
            </label>
            <input
              id={`${id}-other`}
              style={{ ...inputStyle, minHeight: 44 }}
              value={bd.groupLabel}
              onChange={(e) => onPatch({ groupLabel: e.target.value })}
              onBlur={() => setOtherOpen(dimensionSelectValue(bd.dimension) === "diger")}
            />
          </>
        ) : null}
      </div>
      <div className="flex flex-col gap-1" style={{ minWidth: 200 }}>
        <label htmlFor={`${id}-member`} style={fieldLabel}>
          {b.fields.memberLabel}
        </label>
        <input
          id={`${id}-member`}
          style={{ ...inputStyle, minHeight: 44 }}
          value={bd.memberLabel}
          onChange={(e) => onPatch({ memberLabel: e.target.value })}
        />
      </div>
      {problem ? (
        <div
          id={errId}
          role="status"
          style={{ fontSize: "0.75rem", color: "var(--color-warning-text)", alignSelf: "center" }}
          data-testid="kpi-import-bd-missing"
        >
          {b.missing[problem]} — {b.missingHint}
        </div>
      ) : null}
    </div>
  );
}
